import React, { useState, useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { colors, spacing, borderRadius, shadows } from "../../constants";
import { normalizeRole } from "../../utils/assignmentRules";

// Hooks
import { useRaiseQueryFormState } from "../../hooks/queries/raiseQuery/useRaiseQueryFormState";
import { useRaiseQueryUsers } from "../../hooks/queries/raiseQuery/useRaiseQueryUsers";
import { useRaiseQueryProjects } from "../../hooks/queries/raiseQuery/useRaiseQueryProjects";
import { useRaiseQueryAttachments } from "../../hooks/queries/raiseQuery/useRaiseQueryAttachments";
import { useRaiseQueryValidation } from "../../hooks/queries/raiseQuery/useRaiseQueryValidation";
import { useRaiseQuerySubmit } from "../../hooks/queries/raiseQuery/useRaiseQuerySubmit";

// Components
import FormHeader from "./raiseQueryForm/FormHeader";
import BasicInfoSection from "./raiseQueryForm/BasicInfoSection";
import DetailsSection from "./raiseQueryForm/DetailsSection";
import FormFooter from "./raiseQueryForm/FormFooter";

const RaiseQueryForm = ({
  layout = "page",
  isActive = true,
  onCancel,
  onSuccess,
  heading = "Raise New Query",
  visualDescription = "",
  initialRegion = "",
  initialProjectName = "",
  initialQueryType = "",
  initialPriority = "",
  initialAssigneeId = "",
  hasUrlParams = false,
  onProjectAutoSelected,
  onFormDataChange
}) => {
  const isModal = layout === "modal";

  // Get current user from localStorage
  const [currentUser, setCurrentUser] = useState(() => {
    if (typeof window === "undefined") {
      return null;
    }
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch (error) {
      // console.error('Failed to parse user from localStorage', error);
      return null;
    }
  });
  const [accessRights, setAccessRights] = useState({});

  useEffect(() => {
    try {
      const rights = JSON.parse(localStorage.getItem("AccessRights")) || {};
      setAccessRights(rights);
    } catch {
      setAccessRights({});
    }
  }, []);
  const shouldShowSubmit = accessRights["1039"] === "Y";
  // Calculate isRegionSelectionEnabled from currentUser state
  const userRole = normalizeRole(currentUser?.role);
  const isRegionSelectionEnabled = userRole === "CH" || userRole === "MP";

  // File input ref
  const fileInputRef = useRef(null);

  // Form state hook
  const {
    formData,
    setFormData,
    errors,
    setErrors,
    focusedFields,
    setFocusedFields,
    basicInfoExpanded,
    setBasicInfoExpanded,
    handleChange,
    handleRegionChange: handleRegionChangeBase,
    handleFocus,
    handleBlur,
    resetForm: resetFormBase,
    isManualProjectChange,
    lastAutoSelectedRegion
  } = useRaiseQueryFormState({
    initialRegion,
    initialProjectName,
    initialQueryType,
    initialPriority,
    initialAssigneeId,
    visualDescription,
    isActive,
    onFormDataChange
  });

  // Users hook
  const { users, projectUsers, loadingUsers, fetchUsers, fetchProjectUsers } =
    useRaiseQueryUsers({
      isActive,
      currentUser,
      region: formData.region
    });

  // Projects hook
  const { projects, loadingProjects, fetchProjects, resetProjects } =
    useRaiseQueryProjects({
      isActive,
      currentUser,
      region: formData.region,
      initialProjectName,
      onProjectAutoSelected,
      formData,
      setFormData
    });

  // Attachments hook
  const {
    attachments,
    setAttachments,
    attachmentInputKey,
    isDragging,
    handleAttachmentChange,
    handleRemoveAttachment,
    handleDropZoneClick,
    handleDragOver,
    handleDragEnter,
    handleDragLeave,
    handleDrop,
    resetAttachments
  } = useRaiseQueryAttachments(fileInputRef);

  // Validation hook
  const { validate } = useRaiseQueryValidation(formData, currentUser);

  // Submit hook
  const { loading, handleSubmit: handleSubmitBase } = useRaiseQuerySubmit({
    formData,
    projectUsers,
    attachments,
    validate,
    setErrors,
    resetForm: () => {
      resetFormBase();
      resetAttachments();
      resetProjects();
    },
    onSuccess
  });

  // Wrapper for handleRegionChange to include fetchProjects
  const handleRegionChange = (event) => {
    handleRegionChangeBase(event, fetchProjects);
  };

  // Wrapper for resetForm to include attachments and projects reset
  const resetForm = () => {
    resetFormBase();
    resetAttachments();
    resetProjects();
  };

  // Wrapper for handleCancel
  const handleCancel = () => {
    resetForm();
    onCancel?.();
  };

  // Wrapper for handleSubmit
  const handleSubmit = () => {
    handleSubmitBase();
  };

  // Initialize currentUser if not set
  useEffect(() => {
    if (!currentUser && typeof window !== "undefined") {
      try {
        const storedUser = JSON.parse(localStorage.getItem("user"));
        if (storedUser) {
          setCurrentUser(storedUser);
        }
      } catch (error) {
        // Ignore error
      }
    }
  }, [currentUser]);

  // Reset form when isActive changes to false
  useEffect(() => {
    if (!isActive) {
      resetForm();
    }
  }, [isActive]);
  useEffect(() => {
    if (formData.projectCode) {
      fetchProjectUsers(formData.projectCode);
    }
  }, [formData.projectCode]);

  return (
    <>
      <style>
        {`
          .raise-query-form input:not(:focus):hover,
          .raise-query-form select:not(:focus):hover,
          .raise-query-form textarea:not(:focus):hover {
            border-color: ${colors.borderHover} !important;
          }
          .raise-query-form input:disabled,
          .raise-query-form select:disabled {
            cursor: not-allowed;
            opacity: 0.6;
          }
        `}
      </style>
      <Box
        className={`raise-query-form ${isModal ? "raise-query-form--modal" : "raise-query-form--page"}`}
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          backgroundColor: isModal ? "transparent" : colors.backgroundWhite,
          borderRadius: isModal ? borderRadius.modal : borderRadius.xl,
          boxShadow: isModal ? "none" : shadows.xl,
          overflow: "hidden"
        }}
      >
        <FormHeader
          heading={heading}
          isModal={isModal}
          onCancel={handleCancel}
        />

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            padding: `${spacing.sm} ${spacing.md}`,
            paddingBottom: spacing.sm,
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(248,249,250,0.9) 100%)",
            "&::-webkit-scrollbar": {
              width: "10px"
            },
            "&::-webkit-scrollbar-track": {
              background: "#f1f5f9",
              borderRadius: "5px"
            },
            "&::-webkit-scrollbar-thumb": {
              background: "#cbd5e1",
              borderRadius: "5px",
              "&:hover": {
                background: "#0e172a"
              }
            },
            ...(isModal
              ? {
                  overflowY: "auto",
                  maxHeight: "calc(95vh - 180px)"
                }
              : {
                  minHeight: basicInfoExpanded ? "auto" : "auto"
                })
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            gap={spacing.sm}
            sx={{
              pt: spacing.sm,
              pb: 0
            }}
          >
            <BasicInfoSection
              basicInfoExpanded={basicInfoExpanded}
              setBasicInfoExpanded={setBasicInfoExpanded}
              formData={formData}
              errors={errors}
              focusedFields={focusedFields}
              isRegionSelectionEnabled={isRegionSelectionEnabled}
              projects={projects}
              loadingProjects={loadingProjects}
              users={projectUsers}
              loadingUsers={loadingUsers}
              handleChange={handleChange}
              handleRegionChange={handleRegionChange}
              handleFocus={handleFocus}
              handleBlur={handleBlur}
            />

            <DetailsSection
              formData={formData}
              errors={errors}
              focusedFields={focusedFields}
              attachments={attachments}
              isDragging={isDragging}
              fileInputRef={fileInputRef}
              attachmentInputKey={attachmentInputKey}
              handleChange={handleChange}
              handleFocus={handleFocus}
              handleBlur={handleBlur}
              handleDropZoneClick={handleDropZoneClick}
              handleDragEnter={handleDragEnter}
              handleDragOver={handleDragOver}
              handleDragLeave={handleDragLeave}
              handleDrop={handleDrop}
              handleRemoveAttachment={handleRemoveAttachment}
              handleAttachmentChange={handleAttachmentChange}
            />
          </Box>
        </Box>

        <FormFooter
          loading={loading}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          showSubmit={shouldShowSubmit}
        />
      </Box>
    </>
  );
};

export default RaiseQueryForm;
